import { hasAnyPermission } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { SpotExtendedClient } from '@frontend/spot/api';
import { SpotStatusBadge } from '@frontend/spot/badge';
import { spotRequest } from '@frontend/spot/events';
import { Spot, SpotExtended } from '@frontend/spot/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ImageContainerWrapper } from '../../applications/modals/containers/image-wrapper/image-container-wrapper.components';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { ReactiveDetailRegistryEntry, ReactiveDetailView } from '../../modal/reactive-detail-shell/reactive-detail.types';
import {
    SpotActivitySection,
    SpotConstraintsSection,
    SpotGeneralSection,
    SpotIdentificationSection,
    SpotRequirementsSection,
    SpotStockSection
} from './spot-detail-sections';

const SpotDetailsCard = ({ spot }: { spot: SpotExtended }) => {
    const address =
        spot.address ||
        [spot.building, spot.street, spot.number, spot.bus, spot.zip_code, spot.city, spot.province, spot.region, spot.country].filter(Boolean).join(' ');
    const mapQuery = spot.longitude !== null && spot.latitude !== null ? `${spot.latitude},${spot.longitude}` : address;

    return (
        <div className='mb-4 flex w-full gap-3'>
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                <PlatformDetailGrid
                    object={spot}
                    cols={2}
                    order={['name', 'creation_timestamp', 'num_modules', 'num_slots', 'description', 'unique_identifier', 'type', 'status']}
                    keyOverwrite={
                        new Map([
                            ['num_modules', CommonMessage.OBJECTS.MODULE.PLURAL],
                            ['num_slots', CommonMessage.OBJECTS.SLOT.PLURAL]
                        ])
                    }
                    valueOverwrite={
                        new Map([
                            [
                                'type',
                                (obj: SpotExtended) =>
                                    obj.type ? <span>{(obj.type.charAt(0).toUpperCase() + obj.type.slice(1)).replace(/_/g, ' ')}</span> : null
                            ],
                            ['status', (obj: SpotExtended) => <SpotStatusBadge status={obj.status} />]
                        ])
                    }
                />
                {address ? (
                    <div className='border-border-default flex w-full flex-col gap-1 border-t pt-4'>
                        <span className='text-brand-muted text-base font-medium'>{CommonMessage.FORMS.ADDRESS}</span>
                        <div className='flex items-start justify-between gap-3'>
                            <span
                                className='text-foreground-default line-clamp-2 min-w-0 flex-1 text-base font-medium'
                                title={address}>
                                {address}
                            </span>
                            <a
                                target='_blank'
                                rel='noreferrer'
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
                                className='text-brand-primary shrink-0 text-base font-medium'>
                                <FormattedMessage
                                    id='spot-detail-modal.open-in-maps'
                                    defaultMessage='Open in Maps'
                                />
                            </a>
                        </div>
                    </div>
                ) : null}
            </div>
            <ImageContainerWrapper
                entity={spot}
                entity_type={EntityType.SPOT}
            />
        </div>
    );
};

const SpotReactiveTab = ({
    view,
    tab
}: {
    view: ReactiveDetailView<Spot, SpotExtended>;
    tab: 'general' | 'activity' | 'identification' | 'stock' | 'requirements' | 'constraints';
}) => {
    const spot = view.extended;
    if (!spot) return null;
    if (tab === 'general') {
        return (
            <>
                <SpotDetailsCard spot={spot} />
                <SpotGeneralSection spot={spot} />
            </>
        );
    }
    if (tab === 'activity') return <SpotActivitySection spot={spot} />;
    if (tab === 'identification') return <SpotIdentificationSection spot={spot} />;
    if (tab === 'stock') return <SpotStockSection spot={spot} />;
    if (tab === 'requirements') return <SpotRequirementsSection spot={spot} />;
    return <SpotConstraintsSection spot={spot} />;
};

function canReadIdentifications(view: ReactiveDetailView<Spot, SpotExtended>): boolean {
    return (
        view.permissions != null &&
        hasAnyPermission(view.permissions, [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.READ, uid: view.object.account_id }])
    );
}

export const spotReactiveDetail: ReactiveDetailRegistryEntry<Spot, SpotExtended> = {
    entityType: EntityType.SPOT,
    resolveFunction: spotRequest,
    fetchExtended: ({ object }) => SpotExtendedClient.fetchSpotExtended(object.account_id, object.id),
    title: ({ extended, object }) => (
        <>
            {CommonMessage.OBJECTS.SPOT.DISTRI_SPOT}: {extended?.name ?? object.name}
        </>
    ),
    tabs: [
        {
            id: 'general',
            label: CommonMessage.DETAIL.GENERAL,
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='general'
                />
            )
        },
        {
            id: 'activity',
            label: (
                <FormattedMessage
                    id='platform.spot.modal.spot-detail-modal.activity-tab'
                    description='Tab label for SPOT activity (transactions) on the SPOT detail pane'
                    defaultMessage='Activity'
                />
            ),
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='activity'
                />
            )
        },
        {
            id: 'identification',
            label: CommonMessage.OBJECTS.IDENTIFICATION.PLURAL,
            visible: canReadIdentifications,
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='identification'
                />
            )
        },
        {
            id: 'stock',
            label: CommonMessage.OBJECTS.STOCK.DEFAULT,
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='stock'
                />
            )
        },
        {
            id: 'requirements',
            label: (
                <FormattedMessage
                    id='requirements.stock-requirements'
                    defaultMessage='Stock requirements'
                />
            ),
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='requirements'
                />
            )
        },
        {
            id: 'constraints',
            label: CommonMessage.OBJECTS.CONSTRAINT.PLURAL,
            render: (view) => (
                <SpotReactiveTab
                    view={view}
                    tab='constraints'
                />
            )
        }
    ]
};
